import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, useIntl  } from 'react-intl';
import { Button, Modal } from 'antd';
import { CSVLink } from "react-csv"
import LoadingControl from '../controls/Loading';
import DownloadButton from '../controls/DownloadButton';
import { fetchPrinters, enablePrinter, deletePrinters } from '../actions/printers';
import '../App.css';
import PrintersTable from '../controls/PrintersTable';
import { objectWitOnlyProperties } from '../utils/utils';

const PrintersManage = (props) => {

    const [state, setState] = useState({
        enable_apply: false,
        csvData: [],
    });

    const dispatch = useDispatch();
    let printers = useSelector(state => state.printers);
    let has_error = useSelector(state => state.printersHasError);
    let isLoading = useSelector(state => state.printersIsLoading);
    let serversCache = useSelector(state => state.serversCache);

    const intl = useIntl();
    const [modal, contextHolder] = Modal.useModal();

    const data = useMemo(() => {
        return printers.map(record => {
            if (serversCache[record.serverId])
            {
                record['serverName'] = serversCache[record.serverId];
            }
            else
            {
                record['serverName'] = 'serverId = ' + record.serverId;
            } 

            var propertiesPrinter = ["id", "prnName", "prnComment", "prnLocation", "enabled", "serialNumber", "serverName"];

            return objectWitOnlyProperties(record, propertiesPrinter);
        })
    }, [serversCache, printers]);

    const enabled_items = data.filter((record) => record.enabled === 1).map((record) => record.id);

    let printers_to_disable = useRef([]);
    let printers_to_enable = useRef([]);

    function onSelectChange (selectedRowKeys)  {
        const not_enabled_items = data.filter((record) => record.enabled !== 1).map((record) => record.id);

        printers_to_disable.current = enabled_items.filter(x => !selectedRowKeys.includes(x));
        printers_to_enable.current = not_enabled_items.filter(x => selectedRowKeys.includes(x));

        setState({...state, enable_apply : printers_to_disable.current.length > 0 || printers_to_enable.current.length > 0});
    };

    const TOO_MANY_PRINTERS_INSTALLED = '0xe0000003';

    function extractErrorCode(errorText) {
        if (!errorText || typeof errorText !== 'string') {
            return null;
        }

        const hexMatch = errorText.match(/Code:\s*(-?0x[0-9a-f]+)/i);
        if (hexMatch) {
            return hexMatch[1].toLowerCase();
        }

        const decMatch = errorText.match(/Code:\s*(-?\d+)/i);
        if (decMatch) {
            const decValue = Number(decMatch[1]);
            if (!Number.isNaN(decValue)) {
                return ('0x' + (decValue >>> 0).toString(16)).toLowerCase();
            }
        }

        const hresultMatch = errorText.match(/HRESULT:\s*([0-9a-f]+)/i);
        if (hresultMatch) {
            return ('0x' + hresultMatch[1]).toLowerCase();
        }

        return null;
    }

    function isLicensePrinterLimit(errorText) {
        return extractErrorCode(errorText) === TOO_MANY_PRINTERS_INSTALLED;
    }

    async function enablePrinters(printers, enabled) {
        for (const id of printers) {
            try {
                await dispatch(enablePrinter({ id, enabled }));
            } catch (err) {
                const errorText =
                    typeof err?.response?.data === 'string'
                        ? err.response.data
                        : JSON.stringify(err?.response?.data || err?.message || '');

                if (isLicensePrinterLimit(errorText)) {
                    modal.info({
                        title: intl.formatMessage({id: 'LicenseLimitReached'}),
                        content: intl.formatMessage({id: 'LicensePrinterLimit'}),
                        okText: intl.formatMessage({id: 'Ok'}),
                        onOk: () => {
                            window.location.reload();
                        },
                    });
                } else {
                        modal.error({
                            title: intl.formatMessage({id: 'Error'}),
                            content: intl.formatMessage({id: 'FailedToUpdatePrinter'}),
                            okText: intl.formatMessage({id: 'OK'}),
                            onOk: () => {
                                window.location.reload();
                        }
                        });
                }

                break;
            }
        }
    }

    function onApply()
    {
        if (printers_to_disable.current.length > 0)
        {
            enablePrinters(printers_to_disable.current, 0);
            printers_to_disable = [];
        }

        if (printers_to_enable.current.length > 0)
        {
            enablePrinters(printers_to_enable.current, 1);
            printers_to_enable = [];
        }
        
        setState({...state, enable_apply : false});
    }

    function onDelete(id)
    {
        dispatch(deletePrinters([id]));
    }

    let csvLink = useRef(null);
    let dataLoaded = useRef(false);
  
    function update() {
        if ((!printers || printers.length === 0))
        {
            dispatch(fetchPrinters());
        }

        dataLoaded.current = true;
    }  
    
    useEffect(() => {
        if (!dataLoaded.current)
        {
            update();
        }
        else if (csvLink.current)
        {
            csvLink.current.link.click();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.csvData]);    

    function onCSVExportClick()
    {
      setState({...state, csvData: data });

    }

    if(has_error != null)
    {
        return (
            <>
            {contextHolder}
            <div><h3>{has_error.message}</h3></div>
            </>
        );
    }
    else if (isLoading)
    {
        return (
            <>
            {contextHolder}
            <div><LoadingControl/></div>
            </>
        );
    }
    else
    {
        return (
            <>    
            {contextHolder}
            <div className = "history-pane-header">               
                <div className = "range-picker"><Button disabled = {!state.enable_apply} type = "primary" danger = {state.enable_apply} onClick={onApply}><FormattedMessage id="Apply"/></Button></div>
                <CSVLink 
                data={state.csvData}
                separator=";"
                ref={(r) => csvLink.current = r}
                filename="printers.csv"
                className="hidden"
                target="_blank">                
                </CSVLink>
                <DownloadButton onCSVExport = {() => {onCSVExportClick()}}/> 
                <PrintersTable 
                    data = {data} 
                    selected_keys = {enabled_items} 
                    onSelectChange = {onSelectChange}
                    onDelete = {onDelete}
                />
            </div>
            </>
        );
    }
}

const PrintersManagePane = (props) => {  
  
    return (
        <div>
            <PrintersManage/>
        </div>
    );
}
  
export default PrintersManagePane;